import React, { useCallback, useReducer } from 'react'
import { useLocation } from 'react-router-dom'

import {
  ButtonNext as Button,
  Card,
  ClickableNav,
  Flex,
  FormField,
  Input,
  Loader,
  Logo,
  SpacerNext as Spacer,
  TextNext as Text,
} from '@vori/gourmet-components'

import { routes as authRoutes } from '@vori/dashboard-routes/auth'
import { getFirebaseAuth } from '@vori/dashboard-utils/getFirebaseApp'

import { SentryTags } from '@vori/dashboard-constants'
import { AuthErrorSource } from '@vori/dashboard-hooks/useCurrentUser/types'
import { captureException } from '@vori/dashboard-integrations/Sentry/utils'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { ReducerAction, ReducerState } from './types'
import { getDefaultReducerState, reducer } from './utils'

function LoginPage(): JSX.Element {
  const auth = getFirebaseAuth()
  const location = useLocation()
  const emailParam = new URLSearchParams(location.search).get('email')

  const [state, dispatch] = useReducer<
    React.Reducer<ReducerState, ReducerAction>
  >(
    reducer,
    getDefaultReducerState({
      ...(emailParam && { email: emailParam }),
    }),
  )

  const login = useCallback<(state: ReducerState) => void>(
    ({ email, password }) => {
      dispatch({ type: 'SET_LOADING_STATE', loading: true })

      if (auth === null) {
        dispatch({
          type: 'SET_ERROR',
          error: 'An error ocurred, please try again.',
        })
      } else {
        signInWithEmailAndPassword(auth, email, password).catch(
          (error: Error) => {
            captureException(() => error, {
              sentryTag: `${SentryTags.AUTH_PREFIX}${AuthErrorSource.LOGIN}`,
            })

            dispatch({
              type: 'SET_ERROR',
              error: error?.message || 'An error ocurred, please try again.',
            })
          },
        )
      }
    },
    [auth],
  )

  return (
    <Flex center column flex={1}>
      <Spacer size="space.050" />
      <Text size="heading-md">Welcome!</Text>
      <Text size="text-md" variant="secondary">
        Login in to your account to continue
      </Text>
      <Spacer />
      <Card center column size="medium">
        <Logo size="medium" />
        <Spacer />
        <Flex column fullWidth>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              login(state)
            }}
          >
            <FormField fullWidth label="Email">
              <Input
                autoComplete="new-email"
                data-test-id="email-input"
                disabled={state.loading}
                placeholder="email@example.com"
                required
                type="email"
                onChange={({ target: { value } }) => {
                  dispatch({ type: 'SET_EMAIL', email: value })
                }}
                value={state.email}
              />
            </FormField>
            <Spacer />
            <FormField fullWidth error={state.error} label="Password">
              <Input
                autoComplete="new-password"
                data-test-id="password-input"
                disabled={state.loading}
                placeholder="········"
                required
                type="password"
                onChange={({ target: { value } }) => {
                  dispatch({ type: 'SET_PASSWORD', password: value })
                }}
                value={state.password}
              />
            </FormField>
            <Spacer />
            <Button
              disabled={state.loading}
              leftIcon={state.loading ? <Loader variant="secondary" /> : null}
              type="submit"
              fullWidth
              variant="primary"
            >
              {state.loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
          <Spacer size="space.075" />
          <ClickableNav to={authRoutes.passwordRecovery.root.path}>
            Forgot your password?
          </ClickableNav>
        </Flex>
      </Card>
    </Flex>
  )
}

export default LoginPage
